import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import { MdOutlineDownloadForOffline } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ImAttachment, ImEye } from "react-icons/im";
import { LiaHandPointerSolid } from "react-icons/lia";
import { unpinNode } from "../../redux/slices/graphSlice";
import { baseURL } from "../../env/env";
import { processLink } from "../../Utils/LinkUtils";

const ViewerTab = ({
  data,
  playbookHistory,
  setPlaybookHistory,
  setCurrentFurtherRule,
  fetchNode,
  attachmentNodes,
  setStartAgain,
  getAttachmentNodes,
  setAttachmentNodes,
  extractFilename,
  newNode,
  newEdge,
  expandedNodes,
  handlePreview,
  checkIfFileExists,
}) => {
  const [clickedNodeClass, setClickedNodeClass] = useState("");
  const [clickedNodeName, setClickedNodeName] = useState("");
  const [initialCurrentRules, setInitialCurrentRules] = useState([]);
  const [initialPlaybookRuleName, setInitialPlaybookRuleName] = useState("");
  const [attachmentStatus, setAttachmentStatus] = useState({});
  const dispatch = useDispatch();

  const { graphData } = useSelector((state) => state.graph);
  const { edges = [] } = graphData || {};

  const getRelatedEdges = (rule) => {
    const fetchedEdges = [...edges, ...(newEdge ?? [])].filter(
      (edge) => edge.source === rule.id && edge.class === "IsAnswerFor"
    );
    return fetchedEdges.filter((edge) => fetchNode(edge) !== undefined);
  };

  useEffect(() => {
    if (data) {
      setClickedNodeClass(data.class || "");
      setClickedNodeName(data.props?.name || "");
      setInitialCurrentRules(data.relatedPlaybookRules || []);
    }
  }, [data]);

  useEffect(() => {
    if (initialCurrentRules.length > 0) {
      setInitialPlaybookRuleName(initialCurrentRules[0]?.props?.name || "");
      // setCurrentFurtherRule(initialCurrentRules[0]);
      setAttachmentNodes(getAttachmentNodes(initialCurrentRules[0]));
      setPlaybookHistory([]);
    }
  }, [initialCurrentRules]);

  useEffect(() => {
    const checkAttachments = async () => {
      const status = {}; // Store file existence for each node

      // Check if files exist and update status
      for (const node of attachmentNodes) {
        if (node.props.attachment !== "") {
          const exists = await checkIfFileExists(node.props.attachment);
          status[node.id] = exists; // Set the status based on node.id
        }
      }

      setAttachmentStatus(status); // Update state with file existence info
    };

    checkAttachments();
  }, [attachmentNodes]); // Trigger effect when attachmentNodes change

  const handleRuleClick = (edge) => {
    const node = fetchNode(edge);
    if (!node) return;

    setPlaybookHistory((prevHistory) => {
      if (prevHistory.length === 0 && initialCurrentRules.length > 0) {
        return [node, initialCurrentRules[0]];
      }
      return [node, ...prevHistory];
    });

    setCurrentFurtherRule(node);
  };

  const handleGoBack = () => {
    setPlaybookHistory((prevHistory) => {
      // Ensure the history always keeps at least one element
      if (prevHistory.length > 1) {
        const [slicedElement, ...newHistory] = prevHistory;

        if (newHistory.length > 0) {
          if (slicedElement) {
            dispatch(unpinNode(slicedElement.id));
          }
          const previousRule = newHistory[0];
          if (
            expandedNodes &&
            expandedNodes.some((node) => node.id === previousRule.id)
          ) {
            setCurrentFurtherRule(previousRule);
          } else {
            setCurrentFurtherRule(null);
          }
        }
        return newHistory;
      }

      // If only one element remains, keep it in the history
      return prevHistory;
    });
  };

  const handleStartAgain = () => {
    playbookHistory
      .slice(0, -1)
      .forEach((rule) => dispatch(unpinNode(rule.id)));
    setPlaybookHistory([]);
    setStartAgain(true);
    setCurrentFurtherRule(null);
  };

  const isRuleVisited = (edge) => {
    if (!edge || !edge.target) return false;
    return playbookHistory.some((entry) => entry?.id === edge.target);
  };

  const handleDownload = (value) => {
    try {
      const fileUrl = `${baseURL}/uploads/${encodeURIComponent(value)}`;

      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = value;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Error while downloading the file");
      console.error("Error details:", error);
    }
  };

  const handlePathClick = (index) => {
    setCurrentFurtherRule(playbookHistory[index]);
    setPlaybookHistory((prevHistory) => {
      return prevHistory.slice(index + 1);
    });
  };

  return (
    <div className="side-panel-body">
      {initialCurrentRules?.length === 0 && playbookHistory?.length === 0 ? (
        <div className="action-box">
          <p>
            Cannot display Playbook: No Playbook opened, or the opened Playbook
            has no PlaybookRules. Open a Playbook and it will appear here.
          </p>
        </div>
      ) : (
        <div>
          <div className="viewer-controls" style={{ marginBottom: "20px" }}>
            <div className="button-box">
              <ButtonGroup className="w-100">
                <Button onClick={handleGoBack} className="custom-button">
                  Go Back
                </Button>
                <Button onClick={handleStartAgain} className="custom-button">
                  Start Again
                </Button>
              </ButtonGroup>
            </div>
          </div>

          <div style={{ marginTop: "20px" }}>
            <span style={{ textDecoration: "underline", color: "#007bff" }}>
              {clickedNodeClass} {clickedNodeName}
            </span>

            {[...playbookHistory].reverse().map((entry, index) => (
              <React.Fragment key={index}>
                <span style={{ margin: "0 8px" }}>/</span>
                <span
                  onClick={() =>
                    handlePathClick(playbookHistory.length - 2 - index)
                  }
                  style={{
                    textDecoration: "underline",
                    color: "#007bff",
                    cursor: "pointer",
                  }}
                >
                  {entry?.props?.name || ""}
                </span>
              </React.Fragment>
            ))}
          </div>

          {playbookHistory.map((entry, index) => {
            const relatedEdges = getRelatedEdges(entry); // Get edges specific to this rule

            return (
              <div
                key={index}
                className="playbook-rule-box"
                style={{
                  border: "1px solid #ccc",
                  padding: "10px",
                  marginTop: "20px",
                }}
              >
                <div className="side-panel-header h6">
                  {entry?.props?.name || ""}
                </div>
                <div className="action-box">
                  <p>
                    Action:{" "}
                    {entry?.props?.actiontext ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: processLink(entry?.props?.actiontext || ""),
                        }}
                      />
                    ) : (
                      "N/A"
                    )}
                  </p>
                </div>
                <div className="question-box">
                  <p>
                    Question:{" "}
                    {entry?.props?.questiontext ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: processLink(entry?.props?.questiontext || ""),
                        }}
                      />
                    ) : (
                      "N/A"
                    )}
                  </p>
                </div>

                {relatedEdges.map((edge) => {
                  const isAnySourceVisited = relatedEdges.some(
                    (e) => e.source === edge.source && isRuleVisited(e)
                  );

                  return (
                    <Button
                      key={edge.id}
                      onClick={() => handleRuleClick(edge)}
                      disabled={isAnySourceVisited}
                      className={`w-100 view-button ${
                        isRuleVisited(edge) ? "clicked" : ""
                      }`}
                      style={{ marginTop: "20px" }}
                    >
                      {edge?.props?.value || "N/A"}
                    </Button>
                  );
                })}
                {attachmentNodes?.some(
                  (node) =>
                    node.props.attachment !== "" && attachmentStatus[node.id]
                ) && (
                  <div className="attachment-box">
                    <span className="attachment-heading">Attachments:</span>
                    <ul className="p-0">
                      {attachmentNodes.map((node) => {
                        const fileExists = attachmentStatus[node.id];
                        if (node.props.attachment !== "" && fileExists) {
                          return (
                            <li className="attachment-list" key={node.id}>
                              <span className="attachment">
                                <span>
                                  <ImAttachment
                                    style={{ paddingRight: "10px" }}
                                    size={26}
                                  />
                                </span>
                                {extractFilename(node.props.attachment)}
                              </span>
                              <div>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id="tooltip-preview">
                                      Preview attached Document
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    onClick={() =>
                                      handlePreview(
                                        extractFilename(node.props.attachment)
                                      )
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    <ImEye
                                      style={{
                                        paddingLeft: "10px",
                                        color: "#f0503c",
                                      }}
                                      size={26}
                                    />
                                  </span>
                                </OverlayTrigger>

                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id="tooltip-download">
                                      Download attached file
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    title="Download attached file"
                                    onClick={() =>
                                      handleDownload(
                                        extractFilename(node.props.attachment)
                                      )
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    <MdOutlineDownloadForOffline
                                      style={{
                                        paddingLeft: "10px",
                                        color: "#31708f",
                                      }}
                                      size={26}
                                    />
                                  </span>
                                </OverlayTrigger>

                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id="tooltip-show">
                                      Show attachment node in graph
                                    </Tooltip>
                                  }
                                >
                                  <span style={{ cursor: "pointer" }}>
                                    <LiaHandPointerSolid
                                      style={{
                                        paddingLeft: "10px",
                                        color: "#f0503c",
                                      }}
                                      size={26}
                                    />
                                  </span>
                                </OverlayTrigger>
                              </div>
                            </li>
                          );
                        } else {
                          return null; // Return null for nodes without attachment
                        }
                      })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}

          {initialCurrentRules?.length > 0 && playbookHistory?.length === 0 && (
            <div
              className="playbook-rule-box"
              style={{
                border: "1px solid #ccc",
                padding: "10px",
                marginTop: "20px",
              }}
            >
              <div className="side-panel-header h6">
                {initialPlaybookRuleName}
              </div>
              <div className="action-box">
                <p>
                  Action:{" "}
                  {initialCurrentRules[0]?.props?.actiontext ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: processLink(
                          initialCurrentRules[0]?.props?.actiontext || ""
                        ),
                      }}
                    />
                  ) : (
                    "N/A"
                  )}
                </p>
              </div>
              <div className="question-box">
                <p>
                  Question:{" "}
                  {initialCurrentRules[0]?.props?.questiontext ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: processLink(
                          initialCurrentRules[0]?.props?.questiontext || ""
                        ),
                      }}
                    />
                  ) : (
                    "N/A"
                  )}
                </p>
              </div>
              {getRelatedEdges(initialCurrentRules[0])?.length > 0 && (
                <div>
                  {getRelatedEdges(initialCurrentRules[0]).map((edge) => (
                    <Button
                      key={edge.id}
                      onClick={() => handleRuleClick(edge)}
                      className="view-button w-100"
                      style={{ marginTop: "20px" }}
                    >
                      {edge?.props?.value || "N/A"}
                    </Button>
                  ))}
                </div>
              )}
              {attachmentNodes?.some(
                (node) =>
                  node.props?.attachment &&
                  node.props.attachment !== "" &&
                  attachmentStatus[node.id]
              ) && (
                <div className="attachment-box">
                  <span className="attachment-heading">Attachments:</span>
                  <ul className="p-0">
                    {attachmentNodes.map((node) => {
                      const fileExists = attachmentStatus[node.id];

                      if (node.props.attachment !== "" && fileExists) {
                        return (
                          <li className="attachment-list" key={node.id}>
                            <span className="attachment">
                              <span>
                                <ImAttachment
                                  style={{ paddingRight: "10px" }}
                                  size={26}
                                />
                              </span>
                              {extractFilename(node.props.attachment)}
                            </span>
                            <div>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="tooltip-preview">
                                    Preview attached Document
                                  </Tooltip>
                                }
                              >
                                <span
                                  onClick={() =>
                                    handlePreview(
                                      extractFilename(node.props.attachment)
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <ImEye
                                    style={{
                                      paddingLeft: "10px",
                                      color: "#f0503c",
                                    }}
                                    size={26}
                                  />
                                </span>
                              </OverlayTrigger>

                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="tooltip-download">
                                    Download attached file
                                  </Tooltip>
                                }
                              >
                                <span
                                  title="Download attached file"
                                  onClick={() =>
                                    handleDownload(
                                      extractFilename(node.props.attachment)
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <MdOutlineDownloadForOffline
                                    style={{
                                      paddingLeft: "10px",
                                      color: "#31708f",
                                    }}
                                    size={26}
                                  />
                                </span>
                              </OverlayTrigger>

                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="tooltip-show">
                                    Show attachment node in graph
                                  </Tooltip>
                                }
                              >
                                <span style={{ cursor: "pointer" }}>
                                  <LiaHandPointerSolid
                                    style={{
                                      paddingLeft: "10px",
                                      color: "#f0503c",
                                    }}
                                    size={26}
                                  />
                                </span>
                              </OverlayTrigger>
                            </div>
                          </li>
                        );
                      } else {
                        return null; // Return null for nodes without attachment
                      }
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewerTab;
